/**
 * Pure impact evaluation framework for demolition (and any future wall/weapon).
 *
 * Material- and weapon-agnostic: callers supply an ImpactObservation (from a
 * live kinetic contact trace or a synthetic probe) and configurable thresholds.
 * Gates return structured named failures — no implicit pass on missing data
 * when a threshold requires that field.
 */

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

export function dist3(a: Vec3Like, b: Vec3Like): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

/** Deterministic percentile of a non-empty sample (linear interpolation). */
export function percentile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const t = Math.min(1, Math.max(0, p)) * (sortedAsc.length - 1);
  const lo = Math.floor(t);
  const hi = Math.ceil(t);
  if (lo === hi) return sortedAsc[lo];
  const f = t - lo;
  return sortedAsc[lo] * (1 - f) + sortedAsc[hi] * f;
}

/** Pairwise Jaccard similarity of two id sets (empty∩empty → 1). */
export function jaccardIds(a: Iterable<number>, b: Iterable<number>): number {
  const A = new Set(a);
  const B = new Set(b);
  if (A.size === 0 && B.size === 0) return 1;
  let inter = 0;
  for (const id of A) if (B.has(id)) inter += 1;
  const union = A.size + B.size - inter;
  return union > 0 ? inter / union : 1;
}

// ---------------------------------------------------------------------------
// Static graph support (connectivity)
// ---------------------------------------------------------------------------

/**
 * BFS support from grounded nodes across an undirected adjacency map.
 * Returns node ids that have NO path to any grounded id (disconnected islands).
 * Lateral paths keep a node supported — only true islands are unsupported.
 */
export function unsupportedFromGround(
  nodeIds: Iterable<number>,
  adjacency: Map<number, readonly number[]>,
  groundedIds: Iterable<number>,
): number[] {
  const staticSet = new Set(nodeIds);
  const reached = new Set<number>();
  const queue: number[] = [];
  for (const g of groundedIds) {
    if (!staticSet.has(g) || reached.has(g)) continue;
    reached.add(g);
    queue.push(g);
  }
  while (queue.length > 0) {
    const id = queue.shift()!;
    const nbs = adjacency.get(id) ?? [];
    for (const n of nbs) {
      if (!staticSet.has(n) || reached.has(n)) continue;
      reached.add(n);
      queue.push(n);
    }
  }
  const unsupported: number[] = [];
  for (const id of staticSet) {
    if (!reached.has(id)) unsupported.push(id);
  }
  unsupported.sort((a, b) => a - b);
  return unsupported;
}

// ---------------------------------------------------------------------------
// Observation + thresholds
// ---------------------------------------------------------------------------

export interface ImpactReleaseSample {
  id: number;
  position: Vec3Like;
}

/**
 * Snapshot of one impact suitable for property gates.
 * Optional fields: gates that need a missing field either skip or fail
 * according to threshold.require* flags (default: skip when absent).
 */
export interface ImpactObservation {
  /** Intended aim / probe target (world). */
  targetPoint?: Vec3Like;
  /** Actual contact point from the physics engine (world). */
  contactPoint?: Vec3Like;
  projectileRadius?: number;
  projectileMass?: number;
  approachSpeed?: number;
  /** Fraction of ½mv² delivered into the material model (0..1+). */
  energyTransfer?: number;
  /** Energy the resolver actually applied (J). */
  appliedEnergy?: number;
  /** Footprint radius used for locality normalization (m). */
  footprintRadius?: number;
  /** Energy-failed cells released by this hit (direct carve). */
  directReleases?: ImpactReleaseSample[];
  /**
   * Secondary releases: support-collapse / disconnected islands scheduled
   * because of this hit (not direct energy fails).
   */
  secondaryReleases?: ImpactReleaseSample[];
  /** Alias accepted for secondaryReleases. */
  supportReleases?: ImpactReleaseSample[];
  carvedCellCount?: number;
  topologyChanged?: boolean;
}

export interface ImpactEvalThresholds {
  /** Max |contact − target| absolute error (m). */
  maxTargetErrorM?: number;
  /** Max |contact − target| / projectileRadius. */
  maxTargetErrorRadiusNorm?: number;
  /** Max relative |applied − expected| / expected when expected > 0. */
  maxAppliedEnergyRelError?: number;
  /** Absolute tolerance when expected energy is ~0 (J). */
  maxAppliedEnergyAbsWhenExpectedZero?: number;
  /** Max distance of any direct release from contact (m). */
  maxDirectReleaseDistanceM?: number;
  /** Max p95 direct-release distance from contact (m). */
  maxDirectReleaseP95M?: number;
  /** Max (direct distance / footprintRadius). */
  maxDirectReleaseFootprintNorm?: number;
  /** Max secondary release count (e.g. 0 for a local center hole). */
  maxSecondaryCount?: number;
  /** Max distance of any secondary from contact (m). */
  maxSecondaryDistanceM?: number;
  /** Fail when topologyChanged && carvedCellCount === 0. */
  forbidZeroCarveTopology?: boolean;
}

export const DEFAULT_IMPACT_THRESHOLDS: Readonly<Required<ImpactEvalThresholds>> = {
  maxTargetErrorM: 0.35,
  maxTargetErrorRadiusNorm: 1.5,
  maxAppliedEnergyRelError: 0.05,
  maxAppliedEnergyAbsWhenExpectedZero: 1e-6,
  maxDirectReleaseDistanceM: 2.5,
  maxDirectReleaseP95M: 1.8,
  maxDirectReleaseFootprintNorm: 2.5,
  maxSecondaryCount: Number.POSITIVE_INFINITY,
  maxSecondaryDistanceM: Number.POSITIVE_INFINITY,
  forbidZeroCarveTopology: true,
};

export interface ImpactGateFailure {
  gate: string;
  message: string;
  expected?: number;
  actual?: number;
}

export interface ImpactObservationEval {
  pass: boolean;
  failures: ImpactGateFailure[];
  /** |contact − target| when both present. */
  targetErrorM: number | null;
  /** targetErrorM / projectileRadius when both present. */
  targetErrorRadiusNorm: number | null;
  /** ½ m v² × transfer when mass+speed+transfer present. */
  expectedEnergyJ: number | null;
  /** |applied − expected| / expected when expected > 0. */
  appliedEnergyRelError: number | null;
  directReleaseMaxDistM: number | null;
  directReleaseP95DistM: number | null;
  directReleaseMaxFootprintNorm: number | null;
  secondaryCount: number;
  secondaryMaxDistM: number | null;
  zeroCarveTopologyViolation: boolean;
}

export interface ImpactRepeatThresholds {
  /** Max pairwise contact-point spread (max over pairs of |c_i − c_j|). */
  maxContactSpreadM?: number;
  /** Max (max−min) applied energy across observations (J). */
  maxAppliedEnergySpreadJ?: number;
  /** Max coefficient of variation of applied energy (std/mean). */
  maxAppliedEnergyCV?: number;
  /** Min mean pairwise Jaccard of direct-release id sets. */
  minDirectReleaseJaccard?: number;
}

export const DEFAULT_REPEAT_THRESHOLDS: Readonly<Required<ImpactRepeatThresholds>> = {
  maxContactSpreadM: 0.15,
  maxAppliedEnergySpreadJ: 50,
  maxAppliedEnergyCV: 0.08,
  minDirectReleaseJaccard: 0.85,
};

export interface ImpactRepeatabilityEval {
  pass: boolean;
  failures: ImpactGateFailure[];
  contactSpreadM: number | null;
  appliedEnergySpreadJ: number | null;
  appliedEnergyCV: number | null;
  meanDirectReleaseJaccard: number | null;
  observationCount: number;
}

// ---------------------------------------------------------------------------
// Core evaluators
// ---------------------------------------------------------------------------

function secondaryOf(obs: ImpactObservation): ImpactReleaseSample[] {
  return obs.secondaryReleases ?? obs.supportReleases ?? [];
}

export function expectedKineticEnergyJ(
  mass: number,
  speed: number,
  transfer: number,
): number {
  return 0.5 * mass * speed * speed * transfer;
}

function releaseDistances(
  releases: ImpactReleaseSample[],
  origin: Vec3Like,
): number[] {
  return releases.map((r) => dist3(r.position, origin)).sort((a, b) => a - b);
}

function pushFail(
  failures: ImpactGateFailure[],
  gate: string,
  message: string,
  expected?: number,
  actual?: number,
) {
  failures.push({ gate, message, expected, actual });
}

/**
 * Evaluate a single impact observation against configurable thresholds.
 * Missing optional inputs skip the gates that depend on them (unless the
 * observation claims topologyChanged with zero carve — always checked when
 * both fields are present, or when forbidZeroCarveTopology and topology is true).
 */
export function evaluateImpactObservation(
  observation: ImpactObservation,
  thresholds: ImpactEvalThresholds = {},
): ImpactObservationEval {
  const t = { ...DEFAULT_IMPACT_THRESHOLDS, ...thresholds };
  const failures: ImpactGateFailure[] = [];

  let targetErrorM: number | null = null;
  let targetErrorRadiusNorm: number | null = null;
  if (observation.targetPoint && observation.contactPoint) {
    targetErrorM = dist3(observation.targetPoint, observation.contactPoint);
    if (targetErrorM > t.maxTargetErrorM) {
      pushFail(
        failures,
        'targeting.absolute',
        `contact–target error ${targetErrorM.toFixed(4)}m > ${t.maxTargetErrorM}m`,
        t.maxTargetErrorM,
        targetErrorM,
      );
    }
    if (
      observation.projectileRadius != null &&
      observation.projectileRadius > 0
    ) {
      targetErrorRadiusNorm = targetErrorM / observation.projectileRadius;
      if (targetErrorRadiusNorm > t.maxTargetErrorRadiusNorm) {
        pushFail(
          failures,
          'targeting.radiusNorm',
          `contact–target / radius ${targetErrorRadiusNorm.toFixed(3)} > ${t.maxTargetErrorRadiusNorm}`,
          t.maxTargetErrorRadiusNorm,
          targetErrorRadiusNorm,
        );
      }
    }
  }

  let expectedEnergyJ: number | null = null;
  let appliedEnergyRelError: number | null = null;
  if (
    observation.projectileMass != null &&
    observation.approachSpeed != null &&
    observation.energyTransfer != null
  ) {
    expectedEnergyJ = expectedKineticEnergyJ(
      observation.projectileMass,
      observation.approachSpeed,
      observation.energyTransfer,
    );
    if (observation.appliedEnergy != null) {
      if (expectedEnergyJ <= 1e-12) {
        const abs = Math.abs(observation.appliedEnergy);
        appliedEnergyRelError = 0;
        if (abs > t.maxAppliedEnergyAbsWhenExpectedZero) {
          pushFail(
            failures,
            'energy.zeroExpected',
            `expected ~0 J but applied ${observation.appliedEnergy} J`,
            t.maxAppliedEnergyAbsWhenExpectedZero,
            abs,
          );
        }
      } else {
        appliedEnergyRelError =
          Math.abs(observation.appliedEnergy - expectedEnergyJ) / expectedEnergyJ;
        if (appliedEnergyRelError > t.maxAppliedEnergyRelError) {
          pushFail(
            failures,
            'energy.relative',
            `applied-energy relative error ${appliedEnergyRelError.toFixed(4)} > ${t.maxAppliedEnergyRelError}`,
            t.maxAppliedEnergyRelError,
            appliedEnergyRelError,
          );
        }
      }
    }
  }

  const direct = observation.directReleases ?? [];
  const origin = observation.contactPoint ?? observation.targetPoint;
  let directReleaseMaxDistM: number | null = null;
  let directReleaseP95DistM: number | null = null;
  let directReleaseMaxFootprintNorm: number | null = null;

  if (direct.length > 0 && origin) {
    const dists = releaseDistances(direct, origin);
    directReleaseMaxDistM = dists[dists.length - 1] ?? 0;
    directReleaseP95DistM = percentile(dists, 0.95);
    if (directReleaseMaxDistM > t.maxDirectReleaseDistanceM) {
      pushFail(
        failures,
        'locality.directMax',
        `direct max dist ${directReleaseMaxDistM.toFixed(4)}m > ${t.maxDirectReleaseDistanceM}m`,
        t.maxDirectReleaseDistanceM,
        directReleaseMaxDistM,
      );
    }
    if (directReleaseP95DistM > t.maxDirectReleaseP95M) {
      pushFail(
        failures,
        'locality.directP95',
        `direct p95 dist ${directReleaseP95DistM.toFixed(4)}m > ${t.maxDirectReleaseP95M}m`,
        t.maxDirectReleaseP95M,
        directReleaseP95DistM,
      );
    }
    if (observation.footprintRadius != null && observation.footprintRadius > 0) {
      directReleaseMaxFootprintNorm =
        directReleaseMaxDistM / observation.footprintRadius;
      if (directReleaseMaxFootprintNorm > t.maxDirectReleaseFootprintNorm) {
        pushFail(
          failures,
          'locality.directFootprintNorm',
          `direct max / footprint ${directReleaseMaxFootprintNorm.toFixed(3)} > ${t.maxDirectReleaseFootprintNorm}`,
          t.maxDirectReleaseFootprintNorm,
          directReleaseMaxFootprintNorm,
        );
      }
    }
  } else if (direct.length === 0) {
    directReleaseMaxDistM = 0;
    directReleaseP95DistM = 0;
    directReleaseMaxFootprintNorm = 0;
  }

  const secondary = secondaryOf(observation);
  const secondaryCount = secondary.length;
  let secondaryMaxDistM: number | null = null;
  if (secondaryCount > t.maxSecondaryCount) {
    pushFail(
      failures,
      'locality.secondaryCount',
      `secondary count ${secondaryCount} > ${t.maxSecondaryCount}`,
      t.maxSecondaryCount,
      secondaryCount,
    );
  }
  if (secondaryCount > 0 && origin) {
    const dists = releaseDistances(secondary, origin);
    secondaryMaxDistM = dists[dists.length - 1] ?? 0;
    if (secondaryMaxDistM > t.maxSecondaryDistanceM) {
      pushFail(
        failures,
        'locality.secondaryMax',
        `secondary max dist ${secondaryMaxDistM.toFixed(4)}m > ${t.maxSecondaryDistanceM}m`,
        t.maxSecondaryDistanceM,
        secondaryMaxDistM,
      );
    }
  } else if (secondaryCount === 0) {
    secondaryMaxDistM = 0;
  }

  const carved =
    observation.carvedCellCount ??
    (observation.directReleases ? observation.directReleases.length : null);
  let zeroCarveTopologyViolation = false;
  if (
    t.forbidZeroCarveTopology &&
    observation.topologyChanged === true &&
    (carved === 0 || carved === null)
  ) {
    // If topologyChanged with explicit carved=0, or topology with no carve info.
    if (carved === 0 || observation.carvedCellCount === 0) {
      zeroCarveTopologyViolation = true;
      pushFail(
        failures,
        'topology.zeroCarve',
        'topologyChanged with zero carved cells',
        0,
        observation.carvedCellCount ?? 0,
      );
    }
  }

  return {
    pass: failures.length === 0,
    failures,
    targetErrorM,
    targetErrorRadiusNorm,
    expectedEnergyJ,
    appliedEnergyRelError,
    directReleaseMaxDistM,
    directReleaseP95DistM,
    directReleaseMaxFootprintNorm,
    secondaryCount,
    secondaryMaxDistM,
    zeroCarveTopologyViolation,
  };
}

/**
 * Repeatability across multiple independent observations of the "same" shot.
 * Contact spread, applied-energy spread/CV, mean pairwise direct-id Jaccard.
 */
export function evaluateImpactRepeatability(
  observations: ImpactObservation[],
  thresholds: ImpactRepeatThresholds = {},
): ImpactRepeatabilityEval {
  const t = { ...DEFAULT_REPEAT_THRESHOLDS, ...thresholds };
  const failures: ImpactGateFailure[] = [];
  const n = observations.length;

  if (n === 0) {
    return {
      pass: true,
      failures: [],
      contactSpreadM: null,
      appliedEnergySpreadJ: null,
      appliedEnergyCV: null,
      meanDirectReleaseJaccard: null,
      observationCount: 0,
    };
  }

  let contactSpreadM: number | null = null;
  const contacts = observations
    .map((o) => o.contactPoint)
    .filter((p): p is Vec3Like => !!p);
  if (contacts.length >= 2) {
    let max = 0;
    for (let i = 0; i < contacts.length; i += 1) {
      for (let j = i + 1; j < contacts.length; j += 1) {
        max = Math.max(max, dist3(contacts[i], contacts[j]));
      }
    }
    contactSpreadM = max;
    if (contactSpreadM > t.maxContactSpreadM) {
      pushFail(
        failures,
        'repeat.contactSpread',
        `contact spread ${contactSpreadM.toFixed(4)}m > ${t.maxContactSpreadM}m`,
        t.maxContactSpreadM,
        contactSpreadM,
      );
    }
  }

  let appliedEnergySpreadJ: number | null = null;
  let appliedEnergyCV: number | null = null;
  const energies = observations
    .map((o) => o.appliedEnergy)
    .filter((e): e is number => e != null && Number.isFinite(e));
  if (energies.length >= 2) {
    const minE = Math.min(...energies);
    const maxE = Math.max(...energies);
    appliedEnergySpreadJ = maxE - minE;
    if (appliedEnergySpreadJ > t.maxAppliedEnergySpreadJ) {
      pushFail(
        failures,
        'repeat.energySpread',
        `applied-energy spread ${appliedEnergySpreadJ.toFixed(3)}J > ${t.maxAppliedEnergySpreadJ}J`,
        t.maxAppliedEnergySpreadJ,
        appliedEnergySpreadJ,
      );
    }
    const mean = energies.reduce((s, e) => s + e, 0) / energies.length;
    if (mean > 1e-12) {
      const variance =
        energies.reduce((s, e) => s + (e - mean) * (e - mean), 0) / energies.length;
      appliedEnergyCV = Math.sqrt(variance) / mean;
      if (appliedEnergyCV > t.maxAppliedEnergyCV) {
        pushFail(
          failures,
          'repeat.energyCV',
          `applied-energy CV ${appliedEnergyCV.toFixed(4)} > ${t.maxAppliedEnergyCV}`,
          t.maxAppliedEnergyCV,
          appliedEnergyCV,
        );
      }
    } else {
      appliedEnergyCV = 0;
    }
  }

  let meanDirectReleaseJaccard: number | null = null;
  const idSets = observations.map((o) =>
    (o.directReleases ?? []).map((r) => r.id),
  );
  if (idSets.length >= 2) {
    let sum = 0;
    let pairs = 0;
    for (let i = 0; i < idSets.length; i += 1) {
      for (let j = i + 1; j < idSets.length; j += 1) {
        sum += jaccardIds(idSets[i], idSets[j]);
        pairs += 1;
      }
    }
    meanDirectReleaseJaccard = pairs > 0 ? sum / pairs : 1;
    if (meanDirectReleaseJaccard < t.minDirectReleaseJaccard) {
      pushFail(
        failures,
        'repeat.directJaccard',
        `mean direct-release Jaccard ${meanDirectReleaseJaccard.toFixed(4)} < ${t.minDirectReleaseJaccard}`,
        t.minDirectReleaseJaccard,
        meanDirectReleaseJaccard,
      );
    }
  }

  return {
    pass: failures.length === 0,
    failures,
    contactSpreadM,
    appliedEnergySpreadJ,
    appliedEnergyCV,
    meanDirectReleaseJaccard,
    observationCount: n,
  };
}

// ---------------------------------------------------------------------------
// First-fracture commit policy (pure; used by runtime + tests)
// ---------------------------------------------------------------------------

/**
 * Policy for applying a first-fracture resolve:
 * - carved > 0 → commit partition (if any), destroy monolith, fractured=true
 * - carved = 0 + impact partition → commit partition as *latent* damage
 *   discretization only (monolith stays, fractured=false); fatigue keys reuse it
 * - carved = 0 + no partition → leave pristine empty / unchanged
 */
export function decideFirstFractureCommit(opts: {
  carvedCellCount: number;
  hasImpactPartition: boolean;
}): {
  commitPartition: boolean;
  destroyMonolith: boolean;
  fractured: boolean;
  topologyChanged: boolean;
} {
  const carved = opts.carvedCellCount > 0;
  return {
    commitPartition: carved || opts.hasImpactPartition,
    destroyMonolith: carved,
    fractured: carved,
    topologyChanged: carved,
  };
}
